import React from 'react';
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Button } from '../ui/button';
import { BookOpen } from 'lucide-react';
import { FieldHelpPopover } from './IOMappingHelp';
import { HelpDocExport } from '../HelpDocExport';

const CMD_HELP_TOPICS = {
  button_label: {
    title: 'Button Label',
    body: ['The text on the command button shown to users in the Live Tracking Telemetry panel and on Fleet cards.'],
    example: '"Lock Doors", "Request Position", "Reboot".',
  },
  button_color: {
    title: 'Button Colour',
    body: ['The button\'s colour wherever it appears. Use colour to signal intent — red for destructive or high-impact actions, green for safe ones.'],
  },
  command_mode: {
    title: 'Command Mode',
    body: ['• Single Command — one command text sent on press.', '• Command Sequence — several commands sent in order with one press, with an optional delay after each step.'],
    example: 'A pulse output: step 1 turns the output ON, step 2 (after 10000 ms) turns it OFF.',
  },
  command_text: {
    title: 'Command Text',
    body: ['The raw command in your device protocol\'s syntax, sent over the device\'s TCP connection.', 'Teltonika GPRS: setdigout, cpureset, getinfo, getver, getgps, getio, setparam, getparam. Meitrack: C01 (outputs), A10 (locate), F09 (restart).'],
    example: 'setdigout 1 — sets Teltonika digital output 1 high.',
    pitfall: 'Digital Matter devices receive commands via DM Cloud OTA — not supported over TCP.',
  },
  sequence_steps: {
    title: 'Sequence Steps',
    body: ['Commands are sent one after another on the TCP socket. The delay (ms) after each step is applied before the next step is sent — use 0 for instant chaining.', 'A sequence needs at least 2 steps, and every step needs a command.'],
    example: 'C01,0,12222 → wait 10000 ms → C01,0,11111.',
  },
  description: {
    title: 'Description',
    body: ['Optional tooltip text shown when users hover the button — explain what the command actually does to the vehicle.'],
    example: '"Locks the vehicle immobiliser relay".',
  },
  cmd_toggles: {
    title: 'Visibility & Confirmation',
    body: ['Telemetry Panel / Fleet Card control where the button appears.', 'Require Confirmation shows an "Are you sure?" dialog before sending — strongly recommended for immobiliser, reboot or output commands.'],
  },
  target_device: {
    title: 'Send Command To',
    body: ['Routes this command to one of this device\'s linked devices instead of the primary. The command uses the TARGET device\'s protocol syntax and TCP connection.'],
    pitfall: 'If the linked device is later removed, the button will have no valid target.',
  },
};

export const CmdHelpTip = ({ topic }) => {
  const t = CMD_HELP_TOPICS[topic];
  if (!t) return null;
  return <FieldHelpPopover t={t} testid={`cmd-help-tip-${topic}`} />;
};

const Section = ({ title, children }) => (
  <div className="space-y-2">
    <h3 className="text-sm font-semibold text-[#2563eb]">{title}</h3>
    <div className="text-xs text-slate-700 dark:text-slate-300 space-y-2 leading-relaxed">{children}</div>
  </div>
);

const RECIPES = [
  {
    title: 'Immobiliser lock / unlock',
    setup: 'Two Single commands: "Lock" (red) with setdigout 1, "Unlock" (green) with setdigout 0. Enable Require Confirmation on both.',
    result: 'One-press immobiliser control from Live Tracking, with an are-you-sure dialog protecting against accidental taps.',
  },
  {
    title: 'Pulse an output',
    setup: 'Command Sequence: step 1 turns the output ON, step 2 with a 10000 ms delay turns it OFF.',
    result: 'The output activates for exactly 10 seconds with a single button press.',
  },
  {
    title: 'Request a fresh position',
    setup: 'Single command: getgps (Teltonika) or A10 (Meitrack), label "Locate Now", blue.',
    result: 'The device replies with its current position on the next packet.',
  },
  {
    title: 'Command a linked device',
    setup: 'On a primary tracker with a linked secondary unit, set Send Command To → the linked device.',
    result: 'The button lives on the primary device\'s panel but the command is delivered to the linked device using ITS protocol.',
  },
];

const TROUBLESHOOTING = [
  ['Button pressed but nothing happened', 'Commands are queued and delivered over the device\'s live TCP session. An offline device receives the command when it next connects — check the command status (queued / sent) in the Command Center.'],
  ['Command sent but device ignored it', 'Verify the exact protocol syntax — a typo like "setdigout1" (missing space) is silently ignored by most devices. Test the same text from the Command Center first.'],
  ['Add Command button rejects the form', 'A label is always required; Single mode needs command text; Sequence mode needs at least 2 steps and every step must have a command.'],
  ['Changes lost after saving', 'Commands live on the device — press Add Command / Update, then Save Changes. If you forget, the unfinished-form dialog now offers Apply & Save.'],
  ['Reusing commands on another device', 'Use Export / Import in this tab header — imports are only allowed between devices with the SAME protocol.'],
];

export const CommandsHelpPanel = () => (
  <Sheet>
    <SheetTrigger asChild>
      <Button type="button" variant="outline" size="sm" className="h-7 gap-1.5 text-xs" data-testid="commands-help-btn">
        <BookOpen className="w-3.5 h-3.5" /> How Commands work
      </Button>
    </SheetTrigger>
    <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto z-[10000]" data-testid="commands-help-panel">
      <SheetHeader>
        <SheetTitle>How Commands Work</SheetTitle>
        <SheetDescription>Custom buttons that send protocol commands straight to the device.</SheetDescription>
      </SheetHeader>
      <HelpDocExport slug="commands" title="How Commands Work" subtitle="Custom buttons that send protocol commands straight to the device" targetId="help-doc-commands" />
      <div id="help-doc-commands" className="space-y-6 py-4">
        <Section title="1 · What a command is">
          <p>A command is a button users press in <span className="font-medium text-foreground">Live Tracking Telemetry</span> or on <span className="font-medium text-foreground">Fleet cards</span>. Pressing it sends raw protocol text to the device over its existing TCP connection.</p>
          <p>If the device is offline, the command stays <span className="font-medium text-foreground">queued</span> and is delivered automatically when the device next connects. Delivery status (queued / sent) is visible in the Command Center.</p>
        </Section>

        <Section title="2 · Setting one up">
          <p><span className="font-medium text-foreground">1.</span> Give it a <span className="font-medium text-foreground">Button Label</span> and pick a <span className="font-medium text-foreground">colour</span> (red for destructive actions).</p>
          <p><span className="font-medium text-foreground">2.</span> Choose <span className="font-medium text-foreground">Single Command</span> or <span className="font-medium text-foreground">Command Sequence</span> and enter the protocol text.</p>
          <p><span className="font-medium text-foreground">3.</span> Set visibility toggles and enable <span className="font-medium text-foreground">Require Confirmation</span> for anything high-impact.</p>
          <p><span className="font-medium text-foreground">4.</span> Press <span className="font-medium text-foreground">Add Command</span>, then <span className="font-medium text-foreground">Save Changes</span> on the device — commands only take effect after the device is saved.</p>
        </Section>

        <Section title="3 · Protocol syntax">
          <p><span className="font-medium text-foreground">Teltonika</span> — GPRS commands: <span className="font-mono">setdigout, cpureset, getinfo, getver, getgps, getio, setparam, getparam</span>.</p>
          <p><span className="font-medium text-foreground">Meitrack</span> — <span className="font-mono">C01,Speed,ABCDE</span> (outputs), <span className="font-mono">A10</span> (locate), <span className="font-mono">F09</span> (restart).</p>
          <p><span className="font-medium text-foreground">Digital Matter</span> — commands go via DM Cloud OTA and are not supported over TCP.</p>
        </Section>

        <Section title="4 · Sequences">
          <p>A sequence sends multiple commands in order with one button press. The delay (ms) after each step is applied before the next step — use 0 for instant chaining. Minimum 2 steps.</p>
        </Section>

        <Section title="5 · Common recipes">
          {RECIPES.map((s, i) => (
            <div key={i} className="p-2.5 rounded border bg-muted/40 space-y-1">
              <p className="font-medium text-foreground">{s.title}</p>
              <p><span className="font-medium">Setup:</span> {s.setup}</p>
              <p><span className="font-medium">Result:</span> {s.result}</p>
            </div>
          ))}
        </Section>

        <Section title="6 · Why isn't my command working?">
          {TROUBLESHOOTING.map(([q, a], i) => (
            <div key={i}>
              <p className="font-medium text-foreground">{q}</p>
              <p>{a}</p>
            </div>
          ))}
        </Section>
      </div>
    </SheetContent>
  </Sheet>
);
